import { useEffect, useMemo, useState } from "react";
import Header from "../../components/Header/Header";
import Footer from "../../components/footer/Footer";
import SubHeader from "../../components/SubHeader/SubHeader";
import CardProduto, { Produto } from "../../components/CardProduto/CardProduto";
import {
  BiSolidChevronDown,
  BiSolidChevronUp,
  BiSolidStar,
} from "react-icons/bi";
import { MdKeyboardArrowRight } from "react-icons/md";
import { AiOutlinePlus } from "react-icons/ai";
import { RiSubtractFill } from "react-icons/ri";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import style from "./DescricaoDeProduto.module.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../utils/axios/api";
import { Address } from "../../components/EntrepreneurAccountData/types";
import { useCart } from "../../context/CartContext";
import { useChat } from "../../hooks/useChat";
import { Swiper, SwiperSlide } from "swiper/react";
import { useCalculateWithFee } from "../../utils/fee";
import { formatEstimatedDeliveryDate } from "../../utils/estimatedDeliveryDate";
import noImage from '../../assets/no-image.jpg'
import { userIsClient } from "../../utils/userIsLogged";

type ProductWithStoreData = Produto & {
  store: {
    id: string;
    name: string;
    openingHours: { [key: string]: { abertura: string; fechamento: string } };
  };
};
type AddressWithClientId = {
  address: Address;
  clientId: string;
  addressId: string;
  id: string;
};

function DescricaoDeProduto() {
  const { changeNOrders, nOrders } = useCart();
  const calcWithFee = useCalculateWithFee();
  const { id } = useParams<{ id: string }>(); // Tipo de parâmetro definido[]

  const [product, setProduct] = useState<ProductWithStoreData>(
    {} as ProductWithStoreData
  );

  const [produtosRelated, setProdutosRelated] = useState<Produto[]>([]);
  const [addresses, setAddresses] = useState<AddressWithClientId[]>([]);
  const [addressSelected, setAddressSelected] = useState<string | null>(null);
  const [quantidade, setQuantidade] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const defaultDeliveryDateLabel = useMemo(
    () => formatEstimatedDeliveryDate({ language: i18n.language }),
    [i18n.language],
  );

  const galleryUrls = useMemo(() => {
    const main =
      product.image && String(product.image).trim()
        ? String(product.image).trim()
        : null;
    const extras = (product.additionalImages ?? []).filter(
      (u): u is string =>
        typeof u === "string" &&
        Boolean(u.trim()) &&
        u.trim() !== main,
    );
    if (main) return [main, ...extras];
    return extras;
  }, [product.image, product.additionalImages]);

  const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);

  useEffect(() => {
    setSelectedGalleryIndex(0);
  }, [id, product.id, galleryUrls.join("|")]);

  const largePreviewSrc =
    galleryUrls[selectedGalleryIndex] ?? product.image ?? null;

  const navigate = useNavigate();
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState<boolean>(true);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(
    window.innerWidth < 768
  );

  function handleWindowSizeChange() {
    setIsSmallScreen(window.innerWidth < 768);
  }

  useEffect(() => {
    //Verificação se o cliente está logado
    if (localStorage.getItem("clientId")) {
      api
        .get(
          `/favorite/client/${localStorage.getItem("clientId")}/product/${product.id
          }`
        )
        .then((response) => {
          setIsFavorite(response.data.isFavorite);
        });
    } else {
      setIsFavorite(false);
    }

    window.addEventListener("resize", handleWindowSizeChange);
    // Executar uma vez no início para garantir que o estado é atualizado com a largura da janela atual
    handleWindowSizeChange();

    return () => window.removeEventListener("resize", handleWindowSizeChange);
  }, []);

  const weekDayKeys = [
    "domingo",
    "segundaFeira",
    "tercaFeira",
    "quartaFeira",
    "quintaFeira",
    "sextaFeira",
    "sabado",
  ];

  const dayLabels: Record<string, string> = {
    domingo: "Domingo",
    segundaFeira: "Segunda-feira",
    tercaFeira: "Terça-feira",
    quartaFeira: "Quarta-feira",
    quintaFeira: "Quinta-feira",
    sextaFeira: "Sexta-feira",
    sabado: "Sábado",
  };

  function findNextOpeningDay(
    currentDayIndex: number,
    hours: { [key: string]: { abertura: string | null; fechamento: string | null } }
  ): string {
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDay = weekDayKeys[nextDayIndex];
      if (hours[nextDay]?.abertura) {
        return `${dayLabels[nextDay]} às ${hours[nextDay].abertura}`;
      }
    }
    return "em breve";
  }

  function getStoreStatus(hours: {
    [key: string]: { abertura: string | null; fechamento: string | null };
  }): JSX.Element {
    if (!hours || Object.keys(hours).length === 0) {
      return (
        <div className={style.storeStatusClosed}>
          <span className={style.storeStatusClosedLabel}>
            <span className={style.statusDot} style={{ backgroundColor: "#9ca3af" }} />
            Loja indisponível
          </span>
        </div>
      );
    }

    const now = new Date();
    const currentDayIndex = now.getDay();
    const currentDay = weekDayKeys[currentDayIndex];
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    if (hours[currentDay]) {
      const { abertura, fechamento } = hours[currentDay];

      if (!abertura || !fechamento) {
        return (
          <div className={style.storeStatusClosed}>
            <span className={style.storeStatusClosedLabel}>
              <span className={style.statusDot} style={{ backgroundColor: "#dc2626" }} />
              Fechada hoje
            </span>
            <p className={style.storeStatusNext}>
              Abre: {findNextOpeningDay(currentDayIndex, hours)}
            </p>
          </div>
        );
      }

      const [openingHour, openingMinute] = abertura.split(":").map(Number);
      const [closingHour, closingMinute] = fechamento.split(":").map(Number);
      const isOpen =
        (currentHour > openingHour || (currentHour === openingHour && currentMinute >= openingMinute)) &&
        (currentHour < closingHour || (currentHour === closingHour && currentMinute < closingMinute));

      if (isOpen) {
        return (
          <div className={style.storeStatusOpen}>
            <span className={style.statusDot} style={{ backgroundColor: "#16a34a" }} />
            Aberta · Fecha às {fechamento}
          </div>
        );
      }
    }

    return (
      <div className={style.storeStatusClosed}>
        <span className={style.storeStatusClosedLabel}>
          <span className={style.statusDot} style={{ backgroundColor: "#dc2626" }} />
          Fechada no momento
        </span>
        <p className={style.storeStatusNext}>
          Abre: {findNextOpeningDay(currentDayIndex, hours)}
        </p>
      </div>
    );
  }

  const clientId = localStorage.getItem("clientId");
  const userId = localStorage.getItem("userId");
  const { } =
    useChat({
      chatType: "PRODUCT",
      productOrOrderId: id,
      clientId: userId,
    });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function loadData() {
      try {
        const promises = [];
        promises.push(api.get(`/product/${id}`));

        if (clientId) promises.push(api.get(`/address/client/${clientId}`));

        const [responseProduct, responseAddress, responseFee] = await Promise.all(promises);
        setProduct(responseProduct.data);
        setAddresses(responseAddress.data);

        const responseDroductsRelated = await api.get(
          `/product/${responseProduct.data.id}/${responseProduct.data.category}`
        );
        setProdutosRelated(responseDroductsRelated.data.products.slice(0, 24));
      } catch (error) {
        console.error("Erro ao carregar dados do produto:", error);
      }
    }
    loadData();
  }, [id, clientId]);

  const handleBuyFast = async () => {
    if (quantidade > product.quantity) {
      alert("A quantidade deve ser menor do que o estoque do produto");
      return;
    }

    try {
      const payload = {
        clientId: localStorage.getItem("clientId"),
        productId: product.id,
        amount: quantidade,
      };
      await api.post("/cart", payload);

      changeNOrders(nOrders + 1);
      navigate("/carrinho");
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        alert("Este produto já está no carrinho.");
        return;
      }

      if (error.response.status === 403) {
        alert("Os produtos no carrinho devem ser da mesma loja.");
        return;
      }
      alert("Erro ao adicionar produto ao carrinho.");
    }
  };

  const handleAddToCart = async () => {
    if (quantidade > product.quantity) {
      alert("A quantidade deve ser menor do que o estoque do produto");
      return;
    }

    try {
      const payload = {
        clientId: localStorage.getItem("clientId"),
        productId: product.id,
        amount: quantidade,
        addressId: addressSelected,
      };
      await api.post("/cart", payload);
      changeNOrders(nOrders + 1);

      alert("Produto adicionado ao carrinho com sucesso!");
    } catch (error: any) {
      if (error.response && error.response.status === 409) {
        alert("Este produto já está no carrinho.");
        return;
      }

      if (error.response.status === 403) {
        alert("Os produtos no carrinho devem ser da mesma loja.");
        return;
      }
      alert("Erro ao adicionar produto ao carrinho.");
    }
  };

  const handleDecremento = () => {
    if (quantidade > 1) {
      setQuantidade(quantidade - 1);
    }
  };

  const handleIncremento = () => {
    if (quantidade < product.quantity) setQuantidade(quantidade + 1);
  };

  const handleDropdown = () => {
    setIsOpen(!isOpen);
  };

  function formatacaoDeDecimal(value: string): string {
    return calcWithFee(Number(value));
  }

  const hasPromotionalPrice =
    product.promotionalPrice != null &&
    product.originalPrice != null &&
    product.promotionalPrice < product.originalPrice;

  async function handleSharePage() {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("descricaoDeProduto.linkCopiado"));
    } catch {
      toast.error(t("descricaoDeProduto.linkCopiadoErro"));
    }
  }

  function favoriteProduct(event: any) {
    event?.preventDefault();
    const clientId = localStorage.getItem("clientId");

    if (!clientId) return 

    if (isFavorite) {
      api
        .delete(`/favorite/${product.id}/${clientId}`)
        .then(() => {
          setIsFavorite(false);
        })
        .catch(() => {
          alert("Erro ao remover favorito");
        });
    } else {
      api
        .post(`/favorite`, {
          clientId: localStorage.getItem("clientId"),
          productId: product.id,
        })
        .then(() => {
          setIsFavorite(true);
        })
        .catch(() => {
          alert("Erro ao favoritar produto");
        });
    }
  }

  return (
    <div>
      <Header
        boxShadow
        mobileMenuActiveState={isMobileMenuVisible}
        toggleMobileMenu={setIsMobileMenuVisible}
        isSmallScreen={isSmallScreen}
        hamburgerIcon={false}
      />
      <SubHeader />
      <div className={style.pageContent}>
        <div className={style.breadcrumbContainer}>
          <Link to="/" className={style.breadcrumbLink}>{t("descricaoDeProduto.paginaInicial")}</Link>
          <MdKeyboardArrowRight className={style.sideArrowIcon} />
          <Link to="/search/1" className={style.breadcrumbLink}>{t("descricaoDeProduto.busca")}</Link>
          <MdKeyboardArrowRight className={style.sideArrowIcon} />
          <p>{product.name}</p>
        </div>
        <div className={style.topContainer}>
          <div className={style.leftContainer}>
            <div className={style.gallery}>
              <div className={style.thumbnails}>
                {(galleryUrls.length > 0 ? galleryUrls : [null]).map(
                  (url, index) => (
                    <div
                      key={`${url ?? "placeholder"}-${index}`}
                      className={`${style.thumbnailContainer} ${
                        index === selectedGalleryIndex
                          ? style.thumbnailContainerSelected
                          : ""
                      }`}
                      onClick={() => setSelectedGalleryIndex(index)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedGalleryIndex(index);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        src={url ?? noImage}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = noImage;
                        }}
                        alt=""
                        className={style.thumbnail}
                      />
                    </div>
                  ),
                )}
              </div>
              <div className={style.largeImageContainer}>
                <img
                  src={largePreviewSrc ?? noImage}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = noImage;
                  }}
                  alt="imagem grande"
                  className={style.largeImage}
                />
              </div>
            </div>
            <h3 className={style.descricaoHeader}>
              {" "}
              {t("descricaoDeProduto.descricao")}{" "}
            </h3>
            <p className={style.descricao}> {product.description}</p>
          </div>
          <div className={style.rightContainer}>
            <div className={style.produtoPorcentagemInfo}>
              {/*product.promocao && (
                <span className={style.porcentagemInfo}>
                  {product.porcentagemPromocao}% Off
                </span>
              )*/}
            </div>
            <h1 className={style.produtoNome}> {product.name}</h1>
            <div className={style.avaliacaoContainer}>
              <BiSolidStar className={style.iconeAvaliacao} />
              <p className={style.avaliacaoProdutoInfo}>
                {product.rating &&
                  formatacaoDeDecimal(product.rating.toFixed(1))}
              </p>
              <p>
                {" "}
                {product.ratingCount ?? 0} {t("descricaoDeProduto.avaliacoes")}
              </p>
            </div>
            <div className={style.row}>
              <div className={style.precoBloco}>
                {hasPromotionalPrice ? (
                  <>
                    <p className={style.precoOriginal}>
                      R${" "}
                      {formatacaoDeDecimal(product.originalPrice.toFixed(2))}
                    </p>
                    <p className={style.precoProdutoInfo}>
                      R${" "}
                      {formatacaoDeDecimal(
                        product.promotionalPrice!.toFixed(2),
                      )}
                    </p>
                  </>
                ) : (
                  <p className={style.precoProdutoInfo}>
                    R${" "}
                    {formatacaoDeDecimal(
                      product.promotionalPrice
                        ? product.promotionalPrice.toFixed(2)
                        : !product.originalPrice
                          ? "0"
                          : product.originalPrice.toFixed(2),
                    )}
                  </p>
                )}
              </div>
              <div className={style.quantidadeContainer}>
                <p> {t("descricaoDeProduto.quantity")} </p>
                <div className={style.quantidadeButton}>
                  <RiSubtractFill
                    className={style.decremento}
                    onClick={handleDecremento}
                  />
                  <input
                    type="number"
                    className={style.quantidade}
                    value={quantidade}
                    onChange={(e) => setQuantidade(Number(e.target.value))}
                  />
                  <AiOutlinePlus
                    className={style.incremento}
                    onClick={handleIncremento}
                  />
                </div>
              </div>
            </div>
            <div className={style.cardTransportadora}>
              <p className={style.bigText}>
                {t("descricaoDeProduto.vendidoEntregue")}{" "}
                <strong> {product.store?.name} </strong>
              </p>
              <div className={style.bigText}>
                {product.store?.openingHours &&
                  getStoreStatus(product.store?.openingHours)}
              </div>
              <p className={style.smallText}>
                <strong> {t("descricaoDeProduto.estoque")} </strong>{" "}
                {product.quantity} {t('descricaoDeProduto.unit')}{" "}
              </p>
            </div>
            {addresses.map(({ address }) => (
              <div key={address.id} className={style.cardEntregaRetirada}>
                {/* 
                <div className={style.checkboxContainer}>
                  <input type="checkbox" checked={addressSelected === address.id} className={style.checkboxInput} onClick={() => handleAddressSelected(address.id)} />
                </div> 
                */}
                <div>
                  <strong className={style.smallText}>
                    {" "}
                    {t("descricaoDeProduto.receba", {
                      date: defaultDeliveryDateLabel,
                    })}{" "}
                  </strong>
                  <p className={style.smallText}>
                    {" "}
                    {t("descricaoDeProduto.enderecoEntrega")} {address.street},{" "}
                    {address.number}, {address.district}, {address.zipCode},{" "}
                    {address.city}, {address.state}, Brasil
                  </p>
                </div>
              </div>
            ))}
            {/* 
            <div className={style.cardEntregaRetirada}>
              <div className={style.checkboxContainer}>
                <input type="checkbox" className={style.checkboxInput} />
              </div>
              <div>
                <strong className={style.smallText}>
                  {" "}
                  {t("descricaoDeProduto.retire")}{" "}
                </strong>
                <p className={style.smallText}>
                  {" "}
                  {t("descricaoDeProduto.enderecoRetirada")} Rua Eis um Endereço
                  Qualquer Para Lugar nenhum, 456, Vila Nova, 12345-678, São
                  Paulo, SP, Brasil
                </p>
              </div>
            </div> 
            */}
            <div className={style.compartilharContainer}>
              <button
                type="button"
                className={`${style.compartilhar} ${style.compartilharBotao}`}
                onClick={handleSharePage}
              >
                {t("descricaoDeProduto.compartilhar")}
              </button>
              {userIsClient() && <button type="button" onClick={favoriteProduct}>
                {isFavorite ? (
                  <FaHeart
                    className={style.favoritarIcone}
                    style={{ color: "#D8372C" }}
                  />
                ) : (
                  <FaRegHeart className={style.favoritarIcone} />
                )}
              </button>}
            </div>
            {!clientId ? (
              <strong>
                {t("descricaoDeProduto.buy")}
              </strong>
            ) : product.quantity <= 0 ? (
              <strong>{t("descricaoDeProduto.outOfStock")}</strong>
            ) : (
              <>
                <button className={style.comprarButton} onClick={handleBuyFast}>
                  {" "}
                  {t("descricaoDeProduto.compraRapida")}
                </button>
                <button
                  className={style.adicionarCarrinhoButton}
                  onClick={handleAddToCart}
                >
                  {t("descricaoDeProduto.adicionarCarrinho")}
                </button>
              </>
            )}
          </div>
        </div>
        <div className={style.produtosRelacionadoContainer}>
          <div className={style.row}>
            <h1 className={style.produtosRelacionadosHeader}>
              {" "}
              {t("descricaoDeProduto.produtosRelacionados")}
            </h1>
          </div>
          <div className={style.listaDeProdutos}>
            <Swiper
              spaceBetween={16}
              style={{ width: "100%", padding: "8px 4px" }}
              grabCursor={true}
              breakpoints={{
                320: { slidesPerView: 2 },
                640: { slidesPerView: 3 },
                768: { slidesPerView: 4 },
                1024: { slidesPerView: 5 },
                1200: { slidesPerView: 6 },
              }}
            >
              {produtosRelated.map((produtos) => (
                <SwiperSlide key={produtos.id}>
                  <CardProduto key={produtos.id} produto={produtos} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
        <div className={style.informacoesTecnicasContainer}>
          <div
            className={style.dropdownHeaderContainer}
            onClick={handleDropdown}
          >
            <h2 className={style.dropdownHeader}>
              {t("descricaoDeProduto.informacoesTecnicas")}{" "}
            </h2>
            {isOpen ? (
              <BiSolidChevronUp className={style.dropdownIcon} />
            ) : (
              <BiSolidChevronDown className={style.dropdownIcon} />
            )}
          </div>
          {isOpen && (
            <div className={style.dropdownTableContainer}>
              <table className={style.dropdownTable}>
                <tbody>
                  {product.specifications.map((spec) => (
                    <tr key={`${product.id}-${spec.specification}`}>
                      <th className={style.tableCedula}>
                        {spec.specification}
                      </th>
                      <td className={style.tableCedula}>{spec.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {!isSmallScreen && <Footer />}
    </div>
  );
}

export default DescricaoDeProduto;
